import type { CalculateResponse, RetItem } from '@itsrever/returns-api-types'
import { useTranslation } from 'react-i18next'
import { useTranslate } from '@/hooks'
import { BalanceSection } from './balance-section'
import { CheckoutPaymentBalance } from './checkout-payment-balance'
import { ScrollableItems } from './scrollable-items'
import { useAppSelector } from '@/redux/hooks'
import { MediaImageIcon } from '@/ui/icons/media-image'
import { ArrowEmailForwardIcon } from '@/ui/icons/arrow-email-forward'
import { isReturnShippingCharged } from './return-shipping-charged'

interface SummaryProps {
  summary: CalculateResponse
}

export const Summary = ({ summary }: SummaryProps) => {
  const { translate } = useTranslate()
  const { t } = useTranslation()

  const returnedItems = summary?.calculated_summary?.returned_items
  const exchangeItems = summary?.calculated_summary?.exchange_items

  const totalRefundAmount =
    summary?.signed_summary?.return_summary?.total_refund_amount ?? 0
  const totalPaymentAmount =
    summary?.signed_summary?.return_summary?.total_payment_amount ?? 0

  const onlyPayment = totalPaymentAmount > 0 && totalRefundAmount === 0
  const refundAndPayment = totalRefundAmount > 0 && totalPaymentAmount > 0

  const onlyDefectiveOrWrongItems = returnedItems?.every((item) => {
    return (
      item.return_reason === 'DEFECTIVE' || item.return_reason === 'WRONG_ITEM'
    )
  })
  const showDefectiveShippingRefundNote =
    onlyDefectiveOrWrongItems &&
    isReturnShippingCharged(summary?.signed_summary?.return_summary?.line_items)

  return (
    <div className="space-y-3">
      <h1
        className="hidden text-lg font-medium lg:block"
        dangerouslySetInnerHTML={{
          __html: translate('confirmation_page_summary_title')
        }}
      />
      <div className="divide-y divide-solid divide-neutral-200">
        <div className="space-y-6 pb-4">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center rounded-full bg-neutral-100 p-1.5">
                <ArrowEmailForwardIcon className="size-3 -rotate-180" />
              </div>
              <h3 className="text-sm font-medium text-neutral-900">
                {translate('summary_returned_products')}
                <span className="inline-block pl-2 font-normal text-neutral-500">
                  {t('confirmation.summary_items', {
                    count: returnedItems?.length
                  })}
                </span>
              </h3>
            </div>
            <ScrollableItems enabled={(returnedItems?.length ?? 0) > 3}>
              <ul className="space-y-4">
                {returnedItems?.map((item) => (
                  <ProductItem key={item.id} item={item} />
                ))}
              </ul>
            </ScrollableItems>
          </div>
          {exchangeItems && exchangeItems?.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center rounded-full bg-neutral-100 p-1.5">
                  <ArrowEmailForwardIcon className="size-3" />
                </div>
                <h3 className="text-sm font-medium text-neutral-900">
                  {translate('summary_new_products')}
                  <span className="inline-block pl-2 font-normal text-neutral-500">
                    {t('confirmation.summary_items', {
                      count: exchangeItems?.length
                    })}
                  </span>
                </h3>
              </div>
              <ScrollableItems enabled={exchangeItems.length > 3}>
                <ul className="space-y-4">
                  {exchangeItems.map((item) => (
                    <ProductItem key={item.id} item={item} />
                  ))}
                </ul>
              </ScrollableItems>
              <p
                className="text-right text-xs font-normal text-neutral-500"
                dangerouslySetInnerHTML={{
                  __html: translate('summary_items_taxes')
                }}
              />
            </div>
          )}
        </div>
        <div className="space-y-3 divide-y divide-dashed divide-neutral-200">
          {refundAndPayment ? (
            <>
              <BalanceSection summary={summary} variant="refund" />
              <CheckoutPaymentBalance summary={summary} />
            </>
          ) : onlyPayment ? (
            <BalanceSection summary={summary} variant="payment" />
          ) : (
            <BalanceSection summary={summary} variant="refund" />
          )}
        </div>
      </div>
      {showDefectiveShippingRefundNote ? (
        <p
          className="text-sm font-medium text-red-400"
          dangerouslySetInnerHTML={{
            __html: translate('confirmation_only_defective_or_wrong')
          }}
        />
      ) : null}
    </div>
  )
}

const ProductItem = ({ item }: { item: RetItem }) => {
  const { translate } = useTranslate()
  const { i18n } = useTranslation()

  const orderMoneyFormat =
    useAppSelector(
      (store) => store.returnsApi.order.response?.order?.money_format
    ) ?? {}

  return (
    <li className="flex gap-3">
      <div className="relative h-20 w-[4.5rem] shrink-0 overflow-clip rounded-md">
        {item.image_url ? (
          <img
            src={item.image_url}
            alt={item.name}
            height={80}
            width={72}
            className="size-full object-cover"
          />
        ) : (
          <div className="flex size-full min-h-0 items-center justify-center bg-neutral-50 text-neutral-500">
            <MediaImageIcon width={18} height={18} />
          </div>
        )}
        {item.quantity && item.quantity > 1 ? (
          <span className="absolute bottom-1 right-1 rounded-sm bg-white/50 p-0.5 px-1 text-xs font-medium backdrop-blur-sm">
            x {item.quantity}
          </span>
        ) : null}
      </div>
      <div className="flex flex-col gap-1 pb-1">
        <p className="line-clamp-2 max-w-32 text-sm font-normal">{item.name}</p>
        <p className="text-xs font-normal text-neutral-500">
          {item.variant_name}
        </p>
        {item.return_reason ? (
          <p
            className="mt-auto line-clamp-1 w-fit rounded-sm bg-neutral-100 px-2 py-0.5 text-xs font-normal text-neutral-800"
            dangerouslySetInnerHTML={{
              __html: translate(`select_reason_${item.return_reason}`)
            }}
          />
        ) : null}
      </div>
      <p className="ml-auto whitespace-nowrap text-sm font-normal">
        {(
          (item.total ?? 0) / (orderMoneyFormat.amount_multiplied_by ?? 100)
        ).toLocaleString(i18n.language, {
          style: 'currency',
          currency: orderMoneyFormat.currency
        })}
      </p>
    </li>
  )
}
